import { MouseEventHandler, ReactNode } from 'react';
import { Box, SxProps } from '@coolblue-development/freddy';

type LabelColor = 'grey' | 'red' | 'blue' | 'green' | 'orange';
type LabelVariant = 'solid' | 'subtle';
type LabelSize = 'small' | 'medium';

interface LabelProps {
  children: ReactNode;
  color?: LabelColor;
  variant?: LabelVariant;
  size?: LabelSize;
  onClick?: MouseEventHandler<HTMLSpanElement>;
  'data-testid'?: string;
  sx?: SxProps;
}

// Hardcoded hex instead of Freddy's semantic color tokens - several of those tokens
// don't resolve correctly yet (CSD-3337), which silently drops the color instead of
// applying it. Safe to switch back once that's fixed.
const COLOR_STYLES: Record<LabelColor, Record<LabelVariant, { backgroundColor: string; color: string }>> = {
  grey: {
    solid: { backgroundColor: '#666666', color: '#ffffff' },
    subtle: { backgroundColor: '#eeeeee', color: '#666666' }
  },
  red: {
    solid: { backgroundColor: '#e50000', color: '#ffffff' },
    subtle: { backgroundColor: '#ffe5e5', color: '#e50000' }
  },
  orange: {
    solid: { backgroundColor: '#ffae00', color: '#ffffff' },
    subtle: { backgroundColor: '#fff9e6', color: '#ff6600' }
  },
  green: {
    solid: { backgroundColor: '#00b900', color: '#ffffff' },
    subtle: { backgroundColor: '#e0f6e0', color: '#00b900' }
  },
  blue: {
    solid: { backgroundColor: '#0090e3', color: '#ffffff' },
    subtle: { backgroundColor: '#cce9f9', color: '#0090e3' }
  }
};

/**
 * Freddy has no Label/badge component yet. Built on Freddy's Box, mirroring Becky's
 * Label API (color/variant/size) so it's a drop-in replacement at call sites.
 */
export const Label = ({
  children,
  color = 'grey',
  variant = 'solid',
  size = 'medium',
  onClick,
  'data-testid': dataTestId,
  sx
}: LabelProps): JSX.Element => {
  const { backgroundColor, color: textColor } = COLOR_STYLES[color][variant];

  return (
    <Box
      component="span"
      data-testid={dataTestId}
      onClick={onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: '4px',
        padding: size === 'small' ? '2px 4px' : '4px 4px',
        minHeight: size === 'small' ? '21px' : '24px',
        fontSize: size === 'small' ? '12px' : '14px',
        lineHeight: 1.4,
        backgroundColor,
        color: textColor,
        ...sx
      }}
    >
      {children}
    </Box>
  );
};

export default Label;
